import {
	CloseOutlined,
	CommentOutlined,
	ExpandAltOutlined,
	PlusOutlined,
	ShrinkOutlined,
} from '@ant-design/icons';
import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_ICON_SIZE, OSCAR_MARK_SIZE } from '../../constants/AIAgentConstants';
import OscarIconButton from '../shared/OscarIconButton';
import OscarMark from '../shared/OscarMark';
import { useOscarTheme } from '../../context/OscarRoleContext';

interface OscarPanelHeaderProps {
	title?: string;
	/** Start a fresh conversation. Omit to hide the action. */
	onNewChat?: () => void;
	/** Shows New chat but shuts it, e.g. while a conversation is switching. */
	newChatDisabled?: boolean;
	/** Open past conversations. Omit to hide the action. */
	onViewHistory?: () => void;
	/** Grow the panel to full screen. Omit to hide the action. */
	onExpand?: () => void;
	/** Return from fullscreen. Takes the expand slot when provided. */
	onShrink?: () => void;
	/** Fullscreen frames the mark in an accent ring (Figma 14998:125443). */
	framedMark?: boolean;
	onClose?: () => void;
	className?: string;
}

/**
 * Figma nodes 14998:125301 (docked) and 14998:125441 (fullscreen) — the mark,
 * H5/medium title, the action row, a grey/5 rule, and close.
 *
 * Fullscreen differs in two ways only: the mark sits in an accent ring, and the
 * expand action becomes shrink.
 */
export const OscarPanelHeader = ({
	title = 'Oscar',
	onNewChat,
	newChatDisabled,
	onViewHistory,
	onExpand,
	onShrink,
	framedMark,
	onClose,
	className,
}: OscarPanelHeaderProps) => {
	const theme = useOscarTheme();

	return (
		<div className={cn('flex w-full items-center gap-xs px-m py-s', className)}>
			{framedMark ? (
				<span
					className={cn(
						'flex size-9 shrink-0 items-center justify-center rounded-full',
						theme.accentSoft
					)}
				>
					<span className="flex size-7 items-center justify-center rounded-full bg-white">
						<OscarMark size={OSCAR_ICON_SIZE.framedMark} />
					</span>
				</span>
			) : (
				<OscarMark size={OSCAR_MARK_SIZE.header} />
			)}
			<div className="flex min-w-0 flex-1 items-center gap-m">
				<div className="flex min-w-0 flex-1 items-center gap-m">
					<span className="h5medium min-w-0 flex-1 truncate text-character-primary">{title}</span>
					{onNewChat ? (
						<OscarIconButton
							icon={<PlusOutlined />}
							label="New chat"
							onClick={onNewChat}
							disabled={newChatDisabled}
						/>
					) : null}
					{onViewHistory ? (
						<OscarIconButton
							icon={<CommentOutlined />}
							label="Chat history"
							onClick={onViewHistory}
						/>
					) : null}
				</div>
				{onExpand ? (
					<OscarIconButton icon={<ExpandAltOutlined />} label="Expand" onClick={onExpand} />
				) : null}
				{onShrink ? (
					<OscarIconButton icon={<ShrinkOutlined />} label="Exit fullscreen" onClick={onShrink} />
				) : null}
				{onClose ? (
					<div className="flex h-full items-center border-0 border-l border-solid border-grey-5 pl-m">
						<OscarIconButton
							icon={<CloseOutlined />}
							label="Close"
							onClick={onClose}
							size={OSCAR_ICON_SIZE.close}
						/>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default OscarPanelHeader;
